import { InfoTip } from "@/components/ui/info-tip";
import { useState } from "react";
import { CalendarDays, Copy, Plus, RotateCcw } from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  addTeamHoliday,
  adoptTeamHolidays,
  clearTeamHolidays,
  removeTeamHoliday,
  setTeamHolidayObserved,
  updateTeamHoliday,
  type HolidayRule,
  type HolidaySet,
} from "@/lib/tickets";
import { ErrorNote } from "./shared";
import { HolidayEditor } from "./HolidayEditor";
import { HolidayList } from "./HolidayList";

/**
 * The days this team is closed.
 *
 * ⚠️ A TEAM'S LIST WINS WHOLE — its rules replace the organisation's rather than adding to them,
 * the same shape as its working week. A merged list cannot be read off a screen: "closed on
 * exactly these days" is one glance, "the company's list plus two minus one" is a diff somebody
 * has to perform correctly every time they look.
 *
 * ⚠️ Which is why the first action is COPY, not create. The cost of whole-set replacement is
 * that dropping one company holiday means holding your own copy of the others — so adopting
 * starts the list as a copy of the organisation's, and "observe everything except Columbus Day"
 * is one button and one toggle rather than eleven rows retyped.
 *
 * ⚠️ Holidays and hours are SEPARATE overrides. A team may follow the organisation's week and
 * still close on days of its own, or the reverse.
 */
export function TeamHolidaysPanel({
  teamId,
  holidays,
  onChanged,
}: {
  teamId: string;
  holidays: HolidaySet;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // An existing rule, "new", or nothing — one value, so opening a second editor closes the first.
  const [editing, setEditing] = useState<HolidayRule | "new" | null>(null);

  async function run(action: () => Promise<void>, fallback: string) {
    setBusy(true);
    setError(null);

    try {
      await action();
      await onChanged();
      setEditing(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="group/card rounded-lg border border-border">
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <h3 className="flex min-w-0 flex-1 items-center gap-1 text-sm font-medium">
          Holidays
          <InfoTip id="team-holidays" />
        </h3>

        {holidays.isOwnSet ? (
          <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground">
            own list
          </span>
        ) : (
          <span className="shrink-0 text-[11px] text-muted-foreground">
            inherited — organisation list
          </span>
        )}
      </header>

      <div className="space-y-4 p-3">
        <p className="text-xs text-muted-foreground">
          {holidays.isOwnSet ? (
            <>
              Business-hours targets for this team do not advance on these days. Targets set to 24/7
              ignore them. The dates beside each rule are the next three it falls on — check them
              rather than the wording.
            </>
          ) : (
            <>
              This team follows the organisation&apos;s holidays. Give it its own list when the desk
              is staffed on days the organisation is closed, or closed on days it is not.
            </>
          )}
        </p>

        {error && <ErrorNote message={error} />}

        <HolidayList
          holidays={holidays.holidays}
          disabled={busy || !holidays.isOwnSet}
          onToggle={(h, isObserved) =>
            void run(
              () => setTeamHolidayObserved(teamId, h.id, isObserved),
              "That holiday could not be changed.",
            )
          }
          // ⚠️ Editing and removing are offered only on the team's OWN list. While it inherits,
          // the rows on screen belong to the organisation, and letting them be edited here
          // would change every other inheriting team's schedule from a page about one team.
          onEdit={holidays.isOwnSet ? (h) => setEditing(h) : undefined}
          onRemove={
            holidays.isOwnSet
              ? (h) =>
                  void run(
                    () => removeTeamHoliday(teamId, h.id),
                    "That holiday could not be removed.",
                  )
              : undefined
          }
        />

        {editing !== null && holidays.isOwnSet && (
          <HolidayEditor
            existing={editing === "new" ? undefined : editing}
            busy={busy}
            onCancel={() => setEditing(null)}
            onSave={(body) =>
              run(
                () =>
                  editing === "new"
                    ? addTeamHoliday(teamId, body)
                    : updateTeamHoliday(teamId, editing.id, body),
                "That holiday could not be saved.",
              )
            }
          />
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {holidays.isOwnSet ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(() => clearTeamHolidays(teamId), "That list could not be reverted.")
                }
                className="mr-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-input px-3 text-sm text-muted-foreground transition hover:border-ring hover:text-foreground disabled:opacity-50"
              >
                <RotateCcw className="h-4 w-4" aria-hidden />
                Follow the organisation
              </button>

              {editing === null && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setEditing("new")}
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-input px-3 text-sm transition hover:border-ring disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  Add a holiday
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void run(() => adoptTeamHolidays(teamId), "This team could not take its own list.")
              }
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
            >
              <Copy className="h-4 w-4" aria-hidden />
              Start from the organisation&apos;s list
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
